import { Injectable } from "@angular/core";
import { HttpClientService } from "./http-client.service";
import { Observable } from "rxjs";
import { tap } from "rxjs/operators";
import { PrintInsuranceQuote, PrintInsuranceQuoteV2Payload } from "../interfaces/insurance_quoter.interface";
import { InsurerListItem } from "../interfaces/insurer-list.interface";
import { PrintInsuranceResponsiveLetter } from "../interfaces/insurance_responsive_letter.interface";

@Injectable()
export class InsuranceService {
  constructor(private http: HttpClientService) {}

  public getInsurances(account_id): Observable<any> {
    return this.http
      .get("/api/accounts/" + account_id + "/insurances")
      .pipe(tap((_) => console.log("InsuranceService.getInsurances: Ok.")));
  }

  public getInsurers(): Observable<InsurerListItem[]> {
    return this.http
      .get<InsurerListItem[]>("/api/insurances/insurers")
      .pipe(tap((_) => console.log("InsuranceService.getInsurers: Ok.")));
  }

  public getOrdasInsurers(): Observable<any> {
    return this.http
      .get("/api/insurance/insurers")
      .pipe(tap((_) => console.log("InsuranceService.getOrdasInsurers: Ok.")));
  }

  public getAllInsurances(filters): Observable<any> {
    const options = {
      params: filters,
    };
    return this.http
      .get("/api/insurances/all", options)
      .pipe(tap((_) => console.log("InsuranceService.getAllInsurances: Ok.")));
  }

  public getRenewalInsurers(): Observable<any> {
    return this.http
      .get("/api/insurance_renewal_processes/insurers")
      .pipe(tap((_) => console.log("InsuranceService.getRenewalInsurers: Ok.")));
  }

  public getAllInsuranceRenewals(filters): Observable<any> {
    const options = {
      params: filters,
    };
    return this.http
      .get("/api/insurance_renewal_processes/all", options)
      .pipe(tap((_) => console.log("InsuranceService.getAllInsuranceRenewals: Ok.")));
  }

  public getInsuranceRenewal(insurance_id): Observable<any> {
    return this.http
      .get("/api/insurance_renewal_processes/" + insurance_id)
      .pipe(tap((_) => console.log("InsuranceService.getInsuranceRenewal: Ok.")));
  }

  public notifyToClient(insurance_id, term, insurer_options, vehicle_type: string = ""): Observable<any> {
    const options = {
      term,
      insurer_options,
      vehicle_type,
    };
    return this.http
      .post("/api/insurance_renewal_processes/" + insurance_id + "/notify_client", options)
      .pipe(tap((_) => console.log("InsuranceService.notifyToClient: Ok.")));
  }

  public initializeRenewal(insurance_id: string | number, vehicle_type: string = ""): Observable<any> {
    const options = {
      vehicle_type,
    };
    return this.http
      .post("/api/insurance_renewal_processes/" + insurance_id + "/initialize_multiyear_renewal", options)
      .pipe(tap((_) => console.log("InsuranceService.initializeRenewal: Ok.")));
  }

  public validateByClient(renewal_process_id, cash): Observable<any> {
    const options = {
      cash,
    };
    return this.http
      .post("/api/insurance_renewal_processes/" + renewal_process_id + "/validate_by_client", options)
      .pipe(tap((_) => console.log("InsuranceService.validateByClient: Ok.")));
  }

  public cancelRenewalProcess(renewal_process_id, reason): Observable<any> {
    const options = {
      reason,
    };
    return this.http
      .post("/api/insurance_renewal_processes/" + renewal_process_id + "/cancel_process", options)
      .pipe(tap((_) => console.log("InsuranceService.cancelRenewalProcess: Ok.")));
  }

  public finishRenewal(renewal_process_id, cash): Observable<any> {
    const options = {
      cash,
    };
    return this.http
      .post("/api/insurance_renewal_processes/" + renewal_process_id + "/finish_renewal", options)
      .pipe(tap((_) => console.log("InsuranceService.finishRenewal: Ok.")));
  }

  public notifyRenewalOptions(renewal_process_id, renewal_mail): Observable<any> {
    const options = {
      renewal_mail,
    };
    return this.http
      .post("/api/insurance_renewal_processes/" + renewal_process_id + "/notify_renewal_options", options)
      .pipe(tap((_) => console.log("InsuranceService.notifyRenewalOptions: Ok.")));
  }

  public resendNotifyEmail(id_insurance): Observable<any> {
    const options = {
      id_insurance,
    };
    return this.http
      .post("/api/insurance_renewal_processes/" + id_insurance + "/resend_notify_email", options)
      .pipe(tap((_) => console.log("InsuranceService.resendNotifyEmail: Ok.")));
  }

  public saveInsurerSelection(
    renewal_process_id,
    insurer_option_id = null,
    cash,
    insurer_option = null
  ): Observable<any> {
    const options = {
      insurer_option_id,
      insurer_option,
      cash,
    };
    return this.http
      .post("/api/insurance_renewal_processes/" + renewal_process_id + "/choose_insurer", options)
      .pipe(tap((_) => console.log("InsuranceService.saveInsurerSelection: Ok.")));
  }

  public calculateRent(insurance_id: string, amount: number): Observable<any> {
    const options = {
      amount,
    };
    return this.http
      .post("/api/insurance_renewal_processes/" + insurance_id + "/calculate_rent", options)
      .pipe(tap((_) => console.log("InsuranceService.calculateRent: Ok.")));
  }

  public getCountInsurances(): Observable<any> {
    return this.http
      .get("/api/insurance_renewal_processes/count_insurances")
      .pipe(tap((_) => console.log("InsuranceService.getCountInsurances: Ok.")));
  }

  public saveInsurance(renewal_process_id, file, policy_number, initial_date): Observable<any> {
    const options = {
      policy: {
        name: file.name,
        type: file.type,
        original_filename: file.original_filename,
        content: file.content,
        category: "insurance_policy",
      },
      initial_date,
      policy_number,
    };
    return this.http
      .post("/api/insurance_renewal_processes/" + renewal_process_id + "/save_insurance", options)
      .pipe(tap((_) => console.log("InsuranceService.saveInsurance: Ok.")));
  }

  public getAllInsurancesCsv(filters): Observable<any> {
    const options = {
      responseType: "blob" as "json",
      params: filters,
    };
    return this.http
      .get("/api/insurances/all_to_csv", options)
      .pipe(tap((_) => console.log("InsuranceService.getAllInsurancesCsv: Ok.")));
  }

  public addInsurance(account_id, insurance): Observable<any> {
    const options = {
      insurance: insurance,
    };
    return this.http
      .post("/api/accounts/" + account_id + "/insurances", options)
      .pipe(tap((_) => console.log("InsuranceService.addInsurance: Ok.")));
  }

  public updateInsurance(insurance): Observable<any> {
    const options = {
      insurance: insurance,
    };
    return this.http
      .put("/api/insurances/" + insurance.id, options)
      .pipe(tap((_) => console.log("InsuranceService.updateInsurance: Ok.")));
  }

  public getInsuranceRenewalData(account_id: number): Observable<any> {
    return this.http
      .get("/api/insurance_renewal_processes/" + account_id + "/details")
      .pipe(tap((_) => console.log("InsuranceService.getInsuranceRenewalData: Ok.")));
  }

  public sendVerificationCode(insurance_id: number, params: any): Observable<any> {
    return this.http
      .post("/api/insurance_renewal_processes/" + insurance_id + "/send_token", params)
      .pipe(tap((_) => console.log("InsuranceService.sendVerificationCode: Ok.")));
  }

  public verifyVerificationCode(insurance_id: number, params) {
    return this.http
      .post("/api/insurance_renewal_processes/" + insurance_id + "/confirm_code", params)
      .pipe(tap((_) => console.log("InsuranceService.verifyVerificationCode: Ok.")));
  }

  public trackMail(insurance_id: number) {
    return this.http
      .post("/api/insurance_renewal_processes/" + insurance_id + "/track_mail", {})
      .pipe(tap((_) => console.log("InsuranceService.trackMail: Ok.")));
  }

  public printInsuranceQuote(data: PrintInsuranceQuote): Observable<any> {
    let options = data;
    return this.http
      .post("/api/insurances/print_insurance_quote", options, { responseType: "blob" as "json" })
      .pipe(tap((_) => console.log("InsuranceService.printInsuranceQuote: Ok.")));
  }

  public printInsuranceQuoteV2(data: PrintInsuranceQuoteV2Payload): Observable<any> {
    return this.http
      .post("/api/insurances/print_insurance_quote_v2", data, { responseType: "blob" as "json" })
      .pipe(tap((_) => console.log("InsuranceService.printInsuranceQuoteV2: Ok.")));
  }

  public printInsuranceResponsiveLetter(data: PrintInsuranceResponsiveLetter): Observable<any> {
    let options = data;
    return this.http
      .post("/api/insurances/print_insurance_responsive_letter", options, { responseType: "blob" as "json" })
      .pipe(tap((_) => console.log("InsuranceService.printInsuranceResponsiveLetter: Ok.")));
  }
}
